// Play page — full chess game with AI + integrated chess clock
import { ChessGame } from '../../engine/chess';
import { loadSettings, updateSetting } from '../../storage/settings';
import { addGameRecord } from '../../stats/store';
import { checkAchievements } from '../../stats/achievements';
import { playSound } from '../../audio/sound';

let game: ChessGame;
let currentDifficulty: 'easy' | 'medium' | 'hard';
let playerColor: 'w' | 'b' = 'w';
let isAiThinking = false;
let moveCount = 0;
let gameStartTime = 0;
let boardEl: HTMLElement | null = null;
let lastMoveFrom: string | null = null;
let lastMoveTo: string | null = null;
let aiReady = false;
let stockfishWorker: Worker | null = null;
let gameStarted = false;

// ─── Chess Clock State ────────────────────────────────────────
let clockEnabled = true;
let whiteTime = 600; // 10 minutes
let blackTime = 600;
let clockIncrement = 3;
let clockInterval: ReturnType<typeof setInterval> | null = null;
let clockRunning = false;

const TIME_PRESETS = [
  { label: 'Tanpa Timer', time: 0, inc: 0 },
  { label: '1+0 Bullet', time: 60, inc: 0 },
  { label: '2+1', time: 120, inc: 1 },
  { label: '3+0 Blitz', time: 180, inc: 0 },
  { label: '3+2', time: 180, inc: 2 },
  { label: '5+0 Rapid', time: 300, inc: 0 },
  { label: '5+3', time: 300, inc: 3 },
  { label: '10+0', time: 600, inc: 0 },
  { label: '10+3', time: 600, inc: 3 },
  { label: '10+5', time: 600, inc: 5 },
  { label: '15+10', time: 900, inc: 10 },
];

const PIECE_UNICODE: Record<string, string> = {
  'wK': '♔', 'wQ': '♕', 'wR': '♖', 'wB': '♗', 'wN': '♘', 'wP': '♙',
  'bK': '♚', 'bQ': '♛', 'bR': '♜', 'bB': '♝', 'bN': '♞', 'bP': '♟',
};

// ─── AI Engine ─────────────────────────────────────────────────
const DEPTH_MAP = { easy: 2, medium: 6, hard: 12 };

function initAI(): void {
  try {
    stockfishWorker = new Worker('/stockfish.js');
    stockfishWorker.onmessage = () => { aiReady = true; };
    stockfishWorker.postMessage('uci');
    stockfishWorker.postMessage('isready');
  } catch {
    console.warn('Stockfish worker not available');
  }
}

function getAIMove(fen: string): Promise<{ from: string; to: string; promotion?: string }> {
  return new Promise((resolve, reject) => {
    if (!stockfishWorker || !aiReady) {
      reject(new Error('AI not ready'));
      return;
    }
    const depth = DEPTH_MAP[currentDifficulty];
    let resolved = false;
    const handler = (e: MessageEvent) => {
      const msg = e.data as string;
      if (msg.startsWith('bestmove') && !resolved) {
        resolved = true;
        stockfishWorker!.removeEventListener('message', handler);
        const parts = msg.split(' ');
        const bestMove = parts[1];
        if (!bestMove || bestMove.length < 4) { reject(new Error('Invalid')); return; }
        resolve({
          from: bestMove.slice(0, 2),
          to: bestMove.slice(2, 4),
          promotion: bestMove.length > 4 ? bestMove[4] : undefined,
        });
      }
    };
    stockfishWorker.addEventListener('message', handler);
    stockfishWorker.postMessage(`position fen ${fen}`);
    stockfishWorker.postMessage(`go depth ${depth}`);
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        stockfishWorker!.removeEventListener('message', handler);
        reject(new Error('Timeout'));
      }
    }, 10000);
  });
}

function getRandomMove(g: ChessGame): { from: string; to: string; promotion?: string } | null {
  const moves = g.allLegalMoves();
  if (moves.length === 0) return null;
  const captures = moves.filter(m => m.captured);
  if (captures.length > 0 && Math.random() > 0.4) {
    const m = captures[Math.floor(Math.random() * captures.length)];
    return { from: m.from, to: m.to, promotion: m.promotion };
  }
  const m = moves[Math.floor(Math.random() * moves.length)];
  return { from: m.from, to: m.to, promotion: m.promotion };
}

// ─── Render ────────────────────────────────────────────────────
export function renderPlay(): string {
  const settings = loadSettings();
  currentDifficulty = settings.difficulty;

  return `
    <div class="play-page">
      <div class="board-container">

        <!-- Opponent timer (top) -->
        <div class="timer-bar" id="opponent-timer-bar">
          <span class="timer-label" id="opponent-label">♚ AI</span>
          <span class="timer-time" id="opponent-time">--:--</span>
        </div>

        <!-- Game info -->
        <div class="game-info">
          <span class="difficulty" id="diff-label">Level: ${difficultyLabel(currentDifficulty)}</span>
          <span class="turn" id="turn-indicator">Giliranmu</span>
        </div>

        <!-- Board -->
        <div class="board-wrapper" id="board-wrapper">
          <div id="chess-board" class="chess-board"></div>
        </div>

        <!-- Player timer (bottom) -->
        <div class="timer-bar" id="player-timer-bar">
          <span class="timer-label" id="player-label">♔ Kamu</span>
          <span class="timer-time" id="player-time">--:--</span>
        </div>

        <!-- Controls -->
        <div class="game-controls">
          <button class="btn btn-ghost btn-sm" id="btn-undo">↩ Undo</button>
          <button class="btn btn-ghost btn-sm" id="btn-restart">🔄 New</button>
          <button class="btn btn-ghost btn-sm" id="btn-sound">🔊</button>
        </div>

        <!-- Difficulty -->
        <div class="difficulty-selector">
          <span class="text-secondary" style="font-size:0.8rem">Level AI:</span>
          <div class="diff-buttons">
            <button class="btn btn-sm ${currentDifficulty === 'easy' ? 'btn-primary' : 'btn-ghost'}" data-diff="easy">⭐ Easy</button>
            <button class="btn btn-sm ${currentDifficulty === 'medium' ? 'btn-primary' : 'btn-ghost'}" data-diff="medium">⭐⭐ Med</button>
            <button class="btn btn-sm ${currentDifficulty === 'hard' ? 'btn-primary' : 'btn-ghost'}" data-diff="hard">⭐⭐⭐ Hard</button>
          </div>
        </div>

        <!-- Time control -->
        <div class="time-control-selector">
          <span class="text-secondary" style="font-size:0.8rem">⏱️ Waktu:</span>
          <div class="time-presets" id="time-presets">
            ${TIME_PRESETS.map((p, i) => `
              <button class="btn btn-sm ${p.time === 600 && p.inc === 3 ? 'btn-primary' : 'btn-ghost'} time-preset-btn" data-idx="${i}">${p.label}</button>
            `).join('')}
          </div>
        </div>
      </div>
    </div>

    <!-- Color picker -->
    <div id="color-modal" class="color-modal-overlay">
      <div class="color-modal-card">
        <div class="result-icon">♔</div>
        <h2>Pilih Warna</h2>
        <p class="text-secondary mb-md">Mau main sebagai apa?</p>
        <div style="display:flex;gap:16px;justify-content:center">
          <button class="btn btn-primary btn-lg" id="pick-white">♔ Putih</button>
          <button class="btn btn-ghost btn-lg" id="pick-black">♚ Hitam</button>
        </div>
      </div>
    </div>

    <!-- Game over toast -->
    <div id="game-over-toast" class="game-over-toast" style="display:none">
      <div class="toast-content">
        <span class="toast-icon" id="result-icon"></span>
        <div class="toast-text">
          <strong id="result-title"></strong>
          <span id="result-desc" class="text-secondary"></span>
        </div>
        <div class="toast-actions">
          <button class="btn btn-primary btn-sm" id="btn-play-again">🔄 Lagi</button>
        </div>
      </div>
    </div>
  `;
}

// ─── Init ──────────────────────────────────────────────────────
export async function initPlay(): Promise<void> {
  const settings = loadSettings();
  currentDifficulty = settings.difficulty;
  game = new ChessGame();
  moveCount = 0;
  gameStartTime = Date.now();
  isAiThinking = false;
  lastMoveFrom = null;
  lastMoveTo = null;
  gameStarted = false;
  clockEnabled = true;
  clockRunning = false;
  whiteTime = 600;
  blackTime = 600;
  clockIncrement = 3;

  boardEl = document.getElementById('chess-board');

  // Color picker
  const colorModal = document.getElementById('color-modal');
  document.getElementById('pick-white')?.addEventListener('click', () => {
    playerColor = 'w';
    if (colorModal) colorModal.style.display = 'none';
    startGame();
  });
  document.getElementById('pick-black')?.addEventListener('click', () => {
    playerColor = 'b';
    if (colorModal) colorModal.style.display = 'none';
    startGame();
  });

  // Controls
  document.getElementById('btn-undo')?.addEventListener('click', handleUndo);
  document.getElementById('btn-restart')?.addEventListener('click', () => {
    document.getElementById('game-over-toast')!.style.display = 'none';
    document.getElementById('color-modal')!.style.display = 'flex';
  });
  document.getElementById('btn-sound')?.addEventListener('click', handleSoundToggle);
  document.getElementById('btn-play-again')?.addEventListener('click', () => {
    document.getElementById('game-over-toast')!.style.display = 'none';
    document.getElementById('color-modal')!.style.display = 'flex';
  });

  // Difficulty buttons
  document.querySelectorAll('[data-diff]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const diff = (e.target as HTMLElement).dataset.diff as 'easy' | 'medium' | 'hard';
      currentDifficulty = diff;
      updateSetting('difficulty', diff);
      const label = document.getElementById('diff-label');
      if (label) label.textContent = `Level: ${difficultyLabel(diff)}`;
    });
  });

  // Time preset buttons
  document.querySelectorAll('.time-preset-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const idx = parseInt((e.target as HTMLElement).dataset.idx!);
      const preset = TIME_PRESETS[idx];
      clockEnabled = preset.time > 0;
      whiteTime = preset.time;
      blackTime = preset.time;
      clockIncrement = preset.inc;

      // Update button styles
      document.querySelectorAll('.time-preset-btn').forEach(b => b.className = 'btn btn-sm btn-ghost time-preset-btn');
      (e.target as HTMLElement).className = 'btn btn-sm btn-primary time-preset-btn';

      updateTimerDisplay();
    });
  });

  updateSoundButton();
  updateTimerDisplay();
}

function startGame(): void {
  game = new ChessGame();
  moveCount = 0;
  gameStartTime = Date.now();
  isAiThinking = false;
  lastMoveFrom = null;
  lastMoveTo = null;
  gameStarted = true;
  clockRunning = false;

  // Reset clock if enabled
  if (clockEnabled) {
    const preset = TIME_PRESETS.find(p => p.time === whiteTime) || TIME_PRESETS[5];
    whiteTime = preset.time;
    blackTime = preset.time;
    clockIncrement = preset.inc;
  }

  renderBoard();
  initAI();
  updateTimerDisplay();
  startClockIfNeeded();

  // If player is black, AI goes first
  if (playerColor === 'b') {
    setTimeout(() => triggerAI(), 300);
  }
}

// ─── Clock ─────────────────────────────────────────────────────
function startClockIfNeeded(): void {
  if (!clockEnabled || !gameStarted) return;
  clockRunning = true;
  if (clockInterval) clearInterval(clockInterval);
  clockInterval = setInterval(() => {
    if (!clockRunning) return;
    const turn = game.turn();
    if (turn === 'w') {
      whiteTime--;
      if (whiteTime <= 0) {
        whiteTime = 0;
        clockRunning = false;
        clearInterval(clockInterval!);
        clockInterval = null;
        handleTimeout('w');
      }
    } else {
      blackTime--;
      if (blackTime <= 0) {
        blackTime = 0;
        clockRunning = false;
        clearInterval(clockInterval!);
        clockInterval = null;
        handleTimeout('b');
      }
    }
    updateTimerDisplay();
  }, 1000);
}

function stopClock(): void {
  clockRunning = false;
  if (clockInterval) {
    clearInterval(clockInterval);
    clockInterval = null;
  }
}

function switchClock(): void {
  if (!clockEnabled || !clockRunning) return;
  // Add increment to the side that just moved
  const lastMover = game.turn() === 'w' ? 'b' : 'w'; // turn already switched
  if (lastMover === 'w') whiteTime += clockIncrement;
  else blackTime += clockIncrement;
  updateTimerDisplay();
}

function handleTimeout(side: 'w' | 'b'): void {
  playSound('gameover');
  const toast = document.getElementById('game-over-toast');
  const icon = document.getElementById('result-icon');
  const title = document.getElementById('result-title');
  const desc = document.getElementById('result-desc');
  if (!toast || !icon || !title || !desc) return;

  const playerLost = side === playerColor;
  if (playerLost) {
    icon.textContent = '⏰';
    title.textContent = 'Waktu Habis!';
    desc.textContent = 'Kamu kehabisan waktu.';
    toast.className = 'game-over-toast loss';
  } else {
    icon.textContent = '🎉';
    title.textContent = 'Kamu Menang!';
    desc.textContent = 'AI kehabisan waktu!';
    toast.className = 'game-over-toast win';
  }
  toast.style.display = 'block';

  addGameRecord({
    id: Date.now().toString(),
    difficulty: currentDifficulty,
    result: playerLost ? 'loss' : 'win',
    moves: moveCount,
    durationSeconds: Math.round((Date.now() - gameStartTime) / 1000),
    playedAt: new Date().toISOString(),
  }).then(() => checkAchievements());
}

function updateTimerDisplay(): void {
  const playerTimeEl = document.getElementById('player-time');
  const opponentTimeEl = document.getElementById('opponent-time');
  const playerBar = document.getElementById('player-timer-bar');
  const opponentBar = document.getElementById('opponent-timer-bar');
  const playerLabelEl = document.getElementById('player-label');
  const opponentLabelEl = document.getElementById('opponent-label');

  if (!clockEnabled) {
    if (playerTimeEl) playerTimeEl.textContent = '--:--';
    if (opponentTimeEl) opponentTimeEl.textContent = '--:--';
    if (playerBar) playerBar.classList.remove('active', 'low');
    if (opponentBar) opponentBar.classList.remove('active', 'low');
    return;
  }

  const playerTime = playerColor === 'w' ? whiteTime : blackTime;
  const opponentTime = playerColor === 'w' ? blackTime : whiteTime;

  if (playerTimeEl) playerTimeEl.textContent = formatClockTime(playerTime);
  if (opponentTimeEl) opponentTimeEl.textContent = formatClockTime(opponentTime);

  // Labels
  if (playerLabelEl) playerLabelEl.textContent = playerColor === 'w' ? '♔ Kamu (Putih)' : '♚ Kamu (Hitam)';
  if (opponentLabelEl) opponentLabelEl.textContent = playerColor === 'w' ? '♚ AI (Hitam)' : '♔ AI (Putih)';

  // Active highlight
  const isPlayerTurn = game.turn() === playerColor;
  if (playerBar) playerBar.classList.toggle('active', isPlayerTurn && clockRunning);
  if (opponentBar) opponentBar.classList.toggle('active', !isPlayerTurn && clockRunning);

  // Low time warning
  if (playerTimeEl) playerTimeEl.classList.toggle('low', playerTime <= 10 && playerTime > 0);
  if (opponentTimeEl) opponentTimeEl.classList.toggle('low', opponentTime <= 10 && opponentTime > 0);
}

function formatClockTime(seconds: number): string {
  if (seconds <= 0) return '0:00';
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
  return `0:${seconds.toString().padStart(2, '0')}`;
}

// ─── Board Rendering ───────────────────────────────────────────
function renderBoard(): void {
  if (!boardEl) return;
  const board = game.board();
  const flipped = playerColor === 'b';

  let html = '<table class="board-table"><tbody>';
  const rows = flipped ? [0, 1, 2, 3, 4, 5, 6, 7] : [7, 6, 5, 4, 3, 2, 1, 0];
  const cols = flipped ? [7, 6, 5, 4, 3, 2, 1, 0] : [0, 1, 2, 3, 4, 5, 6, 7];

  for (const r of rows) {
    html += '<tr>';
    for (const c of cols) {
      const isLight = (r + c) % 2 === 0;
      const piece = board[r][c];
      const square = String.fromCharCode(97 + c) + (8 - r);
      const pieceKey = piece ? (piece.color + piece.type.toUpperCase()) : '';
      const pieceChar = pieceKey ? PIECE_UNICODE[pieceKey] || '' : '';
      let classes = `square ${isLight ? 'light' : 'dark'}`;
      if (lastMoveFrom === square || lastMoveTo === square) classes += ' last-move';
      html += `<td class="${classes}" data-square="${square}">${pieceChar}</td>`;
    }
    html += '</tr>';
  }
  html += '</tbody></table>';
  boardEl.innerHTML = html;

  boardEl.querySelectorAll('.square').forEach(sq => {
    sq.addEventListener('click', () => handleSquareClick(sq as HTMLElement));
  });

  if (game.isCheck()) {
    const kingSquare = findKing(game.turn());
    if (kingSquare) {
      const el = boardEl.querySelector(`[data-square="${kingSquare}"]`);
      if (el) el.classList.add('in-check');
    }
  }
  updateTurnIndicator();
}

function findKing(color: 'w' | 'b'): string | null {
  const board = game.board();
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const p = board[r][c];
      if (p && p.type === 'k' && p.color === color) {
        return String.fromCharCode(97 + c) + (8 - r);
      }
    }
  }
  return null;
}

// ─── Move Handling ─────────────────────────────────────────────
let selectedSquare: string | null = null;
let legalTargets: string[] = [];

function handleSquareClick(sq: HTMLElement): void {
  if (isAiThinking) return;
  if (game.result() !== 'ongoing') return;
  if (game.turn() !== playerColor) return;

  const square = sq.dataset.square!;

  if (selectedSquare) {
    if (legalTargets.includes(square)) {
      const isPromotion = isPromotionMove(selectedSquare, square);
      if (isPromotion) showPromotionDialog(selectedSquare, square);
      else makePlayerMove(selectedSquare, square);
      clearSelection();
      return;
    }
    clearSelection();
  }

  const piece = getPieceAt(square);
  if (piece && piece.color === game.turn() && piece.color === playerColor) {
    selectedSquare = square;
    sq.classList.add('selected');
    const moves = game.legalMoves(square as any);
    legalTargets = moves.map(m => m.to);
    for (const target of legalTargets) {
      const el = boardEl?.querySelector(`[data-square="${target}"]`);
      if (el) el.classList.add('legal-target');
    }
  }
}

function makePlayerMove(from: string, to: string, promotion?: string): void {
  const move = game.move(from as any, to as any, promotion);
  if (!move) return;

  moveCount++;
  lastMoveFrom = from;
  lastMoveTo = to;

  if (move.captured) playSound('capture');
  else if (move.san === 'O-O' || move.san === 'O-O-O') playSound('castle');
  else if (move.promotion) playSound('promote');
  else playSound('move');

  switchClock();
  renderBoard();

  const result = game.result();
  if (result !== 'ongoing') { handleGameOver(result); return; }
  if (game.isCheck()) playSound('check');

  if (game.turn() !== playerColor) triggerAI();
}

// ─── AI ────────────────────────────────────────────────────────
async function triggerAI(): Promise<void> {
  isAiThinking = true;
  updateTurnIndicator();

  let aiMove: { from: string; to: string; promotion?: string } | null = null;
  try { aiMove = await getAIMove(game.fen()); }
  catch { aiMove = getRandomMove(game); }

  if (aiMove) {
    const delay = currentDifficulty === 'easy' ? 300 : currentDifficulty === 'medium' ? 500 : 800;
    await new Promise(r => setTimeout(r, delay));

    const move = game.move(aiMove.from as any, aiMove.to as any, aiMove.promotion);
    if (move) {
      moveCount++;
      lastMoveFrom = aiMove.from;
      lastMoveTo = aiMove.to;

      if (move.captured) playSound('capture');
      else if (move.san === 'O-O' || move.san === 'O-O-O') playSound('castle');
      else playSound('move');

      switchClock();
      renderBoard();

      const result = game.result();
      if (result !== 'ongoing') { handleGameOver(result); isAiThinking = false; return; }
      if (game.isCheck()) playSound('check');
    }
  }

  isAiThinking = false;
  updateTurnIndicator();
}

// ─── Game Over ─────────────────────────────────────────────────
function handleGameOver(result: string): void {
  isAiThinking = false;
  stopClock();
  playSound('gameover');

  const toast = document.getElementById('game-over-toast');
  const icon = document.getElementById('result-icon');
  const title = document.getElementById('result-title');
  const desc = document.getElementById('result-desc');
  if (!toast || !icon || !title || !desc) return;

  const isWin = result === 'checkmate' && game.turn() !== playerColor;
  const isDraw = result !== 'checkmate';

  if (isWin) {
    icon.textContent = '🎉'; title.textContent = 'Kamu Menang!';
    desc.textContent = 'Hebat! Checkmate!'; toast.className = 'game-over-toast win';
  } else if (isDraw) {
    icon.textContent = '🤝'; title.textContent = 'Seri!';
    desc.textContent = getDrawReason(result); toast.className = 'game-over-toast draw';
  } else {
    icon.textContent = '😢'; title.textContent = 'Kamu Kalah';
    desc.textContent = 'Coba lagi!'; toast.className = 'game-over-toast loss';
  }
  toast.style.display = 'block';

  addGameRecord({
    id: Date.now().toString(),
    difficulty: currentDifficulty,
    result: (isWin ? 'win' : isDraw ? 'draw' : 'loss') as 'win' | 'loss' | 'draw',
    moves: moveCount,
    durationSeconds: Math.round((Date.now() - gameStartTime) / 1000),
    playedAt: new Date().toISOString(),
  }).then(() => checkAchievements());
}

function getDrawReason(result: string): string {
  switch (result) {
    case 'stalemate': return 'Stalemate — tidak ada langkah legal';
    case 'draw_repetition': return 'Draw — posisi terulang 3 kali';
    case 'insufficient_material': return 'Draw — material tidak cukup';
    case 'fifty_move_rule': return 'Draw — 50 langkah tanpa tangkapan';
    default: return 'Permainan seri';
  }
}

// ─── Controls ──────────────────────────────────────────────────
function handleUndo(): void {
  if (isAiThinking) return;
  if (game.history().length < 2) return;
  const aiUndo = game.undo();
  const playerUndo = game.undo();
  if (aiUndo && playerUndo) {
    moveCount = Math.max(0, moveCount - 2);
    const history = game.historyVerbose();
    if (history.length > 0) {
      lastMoveFrom = history[history.length - 1].from;
      lastMoveTo = history[history.length - 1].to;
    } else {
      lastMoveFrom = null;
      lastMoveTo = null;
    }
    clearSelection();
    renderBoard();
  }
}

function handleSoundToggle(): void {
  const settings = loadSettings();
  updateSetting('soundEnabled', !settings.soundEnabled);
  updateSoundButton();
}

function updateSoundButton(): void {
  const settings = loadSettings();
  const btn = document.getElementById('btn-sound');
  if (btn) btn.textContent = settings.soundEnabled ? '🔊' : '🔇';
}

function clearSelection(): void {
  selectedSquare = null;
  legalTargets = [];
  boardEl?.querySelectorAll('.selected').forEach(el => el.classList.remove('selected'));
  boardEl?.querySelectorAll('.legal-target').forEach(el => el.classList.remove('legal-target'));
}

function updateTurnIndicator(): void {
  const el = document.getElementById('turn-indicator');
  if (!el) return;
  if (isAiThinking) {
    el.innerHTML = '<span class="ai-thinking"><span class="spinner"></span> AI thinking...</span>';
  } else if (game.turn() === playerColor) {
    el.textContent = 'Giliranmu';
  } else {
    el.textContent = 'Giliran AI';
  }
}

// ─── Helpers ───────────────────────────────────────────────────
function isPromotionMove(from: string, to: string): boolean {
  const piece = getPieceAt(from);
  if (!piece || piece.type !== 'p') return false;
  return (piece.color === 'w' && to[1] === '8') || (piece.color === 'b' && to[1] === '1');
}

function getPieceAt(square: string): { color: string; type: string } | null {
  const board = game.board();
  const file = square.charCodeAt(0) - 97;
  const rank = 8 - parseInt(square[1]);
  return board[rank]?.[file] || null;
}

function showPromotionDialog(from: string, to: string): void {
  const color = game.turn();
  const pieces = ['q', 'r', 'b', 'n'];
  const labels: Record<string, string> = {
    q: color === 'w' ? '♕' : '♛', r: color === 'w' ? '♖' : '♜',
    b: color === 'w' ? '♗' : '♝', n: color === 'w' ? '♘' : '♞',
  };
  const dialog = document.createElement('div');
  dialog.className = 'promotion-dialog';
  dialog.innerHTML = `<div class="promotion-choices">${pieces.map(p =>
    `<div class="promotion-choice" data-piece="${p}">${labels[p]}</div>`).join('')}</div>`;
  document.body.appendChild(dialog);
  dialog.querySelectorAll('.promotion-choice').forEach(choice => {
    choice.addEventListener('click', () => {
      makePlayerMove(from, to, (choice as HTMLElement).dataset.piece);
      dialog.remove();
    });
  });
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) { dialog.remove(); clearSelection(); }
  });
}

function difficultyLabel(d: string): string {
  switch (d) {
    case 'easy': return '⭐ Easy';
    case 'medium': return '⭐⭐ Medium';
    case 'hard': return '⭐⭐⭐ Hard';
    default: return d;
  }
}
